import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import LogoSettings from "@/components/LogoSettings";

const EMPTY = { nom: "", email: "", password: "", role: "employe", actif: true, permissions: {} };

function PermissionsEditor({ catalogue, defaut, value, onChange }) {
  const groupes = useMemo(() => [...new Set(catalogue.map((p) => p.groupe))], [catalogue]);
  const parGroupe = useMemo(() => Object.fromEntries(groupes.map((g) => [g, catalogue.filter((p) => p.groupe === g)])), [groupes, catalogue]);
  const effective = (k) => (k in value ? value[k] : defaut.includes(k));
  return (
    <div className="col-span-2 space-y-3 border-t border-border pt-4" data-testid="permissions-editor">
      <div className="flex items-center justify-between">
        <Label className="text-foreground font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Autorisations</Label>
        <Button type="button" size="sm" variant="ghost" className="text-xs text-muted-foreground" data-testid="permissions-reset-button" onClick={() => onChange({})}>Valeurs par défaut</Button>
      </div>
      {groupes.map((g) => (
        <div key={g} className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{g}</p>
          {parGroupe[g].map((p) => (
            <div key={p.key} className="flex items-center justify-between gap-3 bg-background border border-border rounded-lg px-3 py-2">
              <span className="text-sm text-foreground/90">{p.label}</span>
              <Switch checked={effective(p.key)} onCheckedChange={(v) => onChange({ ...value, [p.key]: v })} data-testid={`perm-switch-${p.key}`} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [permsMeta, setPermsMeta] = useState({ catalogue: [], defaut_employe: [] });

  const refresh = useCallback(() => api.get("/users").then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement")), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { api.get("/permissions").then((r) => setPermsMeta(r.data)).catch(() => {}); }, []);

  const openForm = (u) => {
    setEditing(u || null);
    setForm(u ? { nom: u.nom, email: u.email, password: "", role: u.role, actif: u.actif, permissions: u.role === "admin" ? {} : { ...u.permissions } } : EMPTY);
    setDialog(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const permissions = form.role === "admin" ? {} : form.permissions;
      if (editing) {
        const payload = { nom: form.nom, role: form.role, actif: form.actif, permissions };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Utilisateur modifié");
      } else {
        await api.post("/users", { nom: form.nom, email: form.email, password: form.password, role: form.role, permissions });
        toast.success("Utilisateur créé");
      }
      setDialog(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Utilisateur supprimé");
      setToDelete(null);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="utilisateurs-loading">Chargement…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="utilisateurs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestion des comptes (réservé à l'administrateur)</p>
        </div>
        <Button data-testid="add-user-button" onClick={() => openForm(null)} className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un utilisateur
        </Button>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Nom</TableHead>
                <TableHead className="text-muted-foreground">Email</TableHead>
                <TableHead className="text-muted-foreground">Rôle</TableHead>
                <TableHead className="text-muted-foreground">Statut</TableHead>
                <TableHead className="text-muted-foreground">Autorisations</TableHead>
                <TableHead className="text-muted-foreground">Créé le</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium text-foreground">{u.nom}</TableCell>
                  <TableCell className="text-foreground/80">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.role === "admin" ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-blue-500/40 text-blue-600 dark:text-blue-400"}>
                      {u.role === "admin" ? "Administrateur" : "Employé"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={u.actif ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-red-500/40 text-red-600 dark:text-red-400"} data-testid={`user-status-${u.id}`}>
                      {u.actif ? "Actif" : "Désactivé"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground" data-testid={`user-perms-${u.id}`}>
                    {u.role === "admin" ? "Toutes" : `${Object.values(u.permissions || {}).filter(Boolean).length} / ${permsMeta.catalogue.length || Object.keys(u.permissions || {}).length}`}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400" data-testid={`edit-user-${u.id}`} onClick={() => openForm(u)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {u.id !== me?.id && (
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-user-${u.id}`} onClick={() => setToDelete(u)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border text-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="user-dialog-title">{editing ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2">
              <Label className="text-foreground/80">Nom</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="user-nom-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Email</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="bg-background border-border text-foreground disabled:opacity-50" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="employe">Employé</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center gap-3 col-span-2">
                <Switch checked={form.actif} onCheckedChange={(v) => setForm({ ...form, actif: v })} data-testid="user-actif-switch" />
                <Label className="text-foreground/80">Compte actif</Label>
              </div>
            )}
            {form.role === "admin" ? (
              <p className="col-span-2 text-xs text-muted-foreground border-t border-border pt-3" data-testid="permissions-admin-note">L'administrateur dispose de toutes les autorisations.</p>
            ) : (
              <PermissionsEditor catalogue={permsMeta.catalogue} defaut={permsMeta.defaut_employe} value={form.permissions} onChange={(p) => setForm({ ...form, permissions: p })} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-border text-foreground/80">Annuler</Button>
            <Button onClick={save} disabled={busy || !form.nom || (!editing && (!form.email || !form.password))} data-testid="save-user-button" className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LogoSettings />

      <ConfirmDelete open={!!toDelete} onOpenChange={() => setToDelete(null)} onConfirm={remove} title={toDelete?.nom} busy={busy} />
    </div>
  );
}
